import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Field } from 'redux-form';

import Default from 'components/Input/Default';
import Select from 'components/Input/Select';
import SquareOptions from 'components/Input/SquareOptions';
import SquareSelectOptions from 'components/Input/SquareSelectOptions';
import Label from 'components/Label';
import {
    BUILD_PERIODS,
    ENERGY_CONSUMPTIONS,
    LIGHTNESS_VALUES,
    PEACEFULNESS_VALUES,
    RELATIVE_PERCEPTIONS,
    RENOVATION_LEVELS,
    ROOF_VALUES,
    STANDING_VALUES,
    TRANSPORT_PROXIMITIES,
} from 'constants/Global';
import { isApartment, isHouse, isProvince } from 'helpers/FormInformations';
import { translate } from 'helpers/Translate';

/**
 * Normalize value for Redux storage.
 * @param {String} value Value to normalize
 * @return {Number} Normalized value
 */
export const normalizeNumber = (value) => numeral(value).value();

/**
 * Format value for display
 * @param {Number} value Value to format
 */
export const formatNumber = (value) => String(value);

const More = ({ values }) => {
    const province = isProvince(values);
    const apartment = isApartment(values);
    const house = isHouse(values);
    const perceptionLabel = apartment
        ? translate("Qualité de l'appartement")
        : translate('Qualité de la maison');
    const perceptionSublabel = apartment
        ? translate("Par rapport aux autres appartements de l'immeuble")
        : translate('par rapport aux autres maisons de votre ilôt');

    return (
        <div data-step-more>
            {!province && !house && (
                <div className="row-container row-container--vertical row-container--padding-bottom">
                    <div className="row-container__label">
                        <Label
                            labelFor="build_period"
                            text={translate('Période de construction')}
                        />
                    </div>
                    <div className="row-container__field">
                        <Field
                            component={Select}
                            name="build_period"
                            normalize={normalizeNumber}
                            format={formatNumber}
                            options={BUILD_PERIODS}
                            emptyOption
                            emptyValue={-1}
                        />
                    </div>
                </div>
            )}
            {(province || house) && (
                <div className="row-container row-container--vertical row-container--padding-bottom">
                    <div className="row-container__label">
                        <Label labelFor="build_date" text={translate('Année de construction')} />
                    </div>
                    <div className="row-container__field">
                        <Field
                            component={Default}
                            name="build_date"
                            type="number"
                            placeholder={translate('Ex : 1986')}
                            autoFocus={values.build_date === undefined}
                        />
                    </div>
                </div>
            )}
            {house && (
                <div className="row-container row-container--vertical row-container--padding-bottom">
                    <div className="row-container__label">
                        <Label text={translate('Diagnostic de Performance Energétique')} />
                    </div>
                    <div className="row-container__field">
                        <Field
                            component={Select}
                            name="energy_consumption"
                            options={ENERGY_CONSUMPTIONS}
                            emptyOption
                        />
                    </div>
                </div>
            )}
            <div className="row-container row-container--vertical row-container--padding-bottom">
                <div className="row-container__label">
                    <Label labelFor="renovation_level" text={translate('État du bien')} />
                </div>
                <div className="row-container__field">
                    <Field component={Select} name="renovation_level" options={RENOVATION_LEVELS} />
                </div>
            </div>
            {province && !house && (
                <div className="row-container row-container--vertical row-container--padding-bottom">
                    <div className="row-container__label">
                        <Label text={translate('Standing général du bien')} />
                    </div>
                    <div className="row-container__field">
                        <SquareOptions options={STANDING_VALUES} name="standing" />
                    </div>
                </div>
            )}
            <div className="row-container row-container--vertical row-container--padding-bottom">
                <div className="row-container__label">
                    <Label text={perceptionLabel} subText={perceptionSublabel} inlineSub={false} />
                </div>
                <div className="row-container__field">
                    <SquareOptions options={RELATIVE_PERCEPTIONS} name="relative_perception" />
                </div>
            </div>
            {province && (
                <div>
                    <div className="row-container row-container--vertical row-container--padding-bottom">
                        <div className="row-container__label">
                            <Label text={translate('Luminosité')} />
                        </div>
                        <div className="row-container__field">
                            <SquareSelectOptions options={LIGHTNESS_VALUES} name="lightness" />
                        </div>
                    </div>
                    <div className="row-container row-container--vertical row-container--padding-bottom">
                        <div className="row-container__label">
                            <Label text={translate('Calme')} />
                        </div>
                        <div className="row-container__field">
                            <SquareSelectOptions
                                options={PEACEFULNESS_VALUES}
                                name="peacefulness"
                            />
                        </div>
                    </div>
                    <div className="row-container row-container--vertical row-container--padding-bottom">
                        <div className="row-container__label">
                            <Label text={translate('Proximité des transports')} />
                        </div>
                        <div className="row-container__field">
                            <SquareSelectOptions
                                options={TRANSPORT_PROXIMITIES}
                                name="transport_proximity"
                            />
                        </div>
                    </div>
                    {house && (
                        <div className="row-container row-container--vertical row-container--padding-bottom">
                            <div className="row-container__label">
                                <Label text={translate('Qualité de la toiture')} />
                            </div>
                            <div className="row-container__field">
                                <SquareOptions options={ROOF_VALUES} name="roof_quality" />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

More.displayName = 'More';

More.propTypes = {
    values: PropTypes.shape({}),
};

/**
 * More Functional Component.
 */
export default More;
